import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Timer, AlertCircle, CheckCircle2 } from 'lucide-react';
import { supabase } from '../lib/supabase';

const RegisterPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    passwordConfirm: '',
    fullName: '',
    organization: '',
  });
  // emailTouched は未使用なので削除

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setFormData({ ...formData, email });
    // setEmailTouched(true); は削除
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (formData.password !== formData.passwordConfirm) {
        throw new Error('パスワードが一致しません');
      }

      const email = formData.email.toLowerCase().trim();
      console.log('Registering user:', email);

      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: email,
        password: formData.password.trim(),
        options: {
          data: {
            full_name: formData.fullName,
            organization: formData.organization,
          },
          emailRedirectTo: 'https://analysis.jrpo.jp/subscription',
        },
      });

      if (signUpError) throw signUpError;
      if (!authData.user) throw new Error('ユーザー登録に失敗しました');

      console.log('User registered successfully:', authData.user.id);

      await new Promise(resolve => setTimeout(resolve, 2000));

      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', authData.user.id)
        .single();

      if (!existingProfile) {
        const { error: profileError } = await supabase
          .from('profiles')
          .insert({
            id: authData.user.id,
            email: email,
            full_name: formData.fullName,
            organization: formData.organization,
          });

        if (profileError && !profileError.message.includes('duplicate key')) {
          console.warn('Profile creation error:', profileError);
        }
      }

      const { data: existingRole } = await supabase
        .from('user_roles')
        .select('id')
        .eq('user_id', authData.user.id)
        .eq('role', 'user')
        .single();

      if (!existingRole) {
        const { error: roleError } = await supabase
          .from('user_roles')
          .insert({
            user_id: authData.user.id,
            role: 'user',
          });

        if (roleError) {
          console.warn('Role creation error:', roleError);
        }
      }

      try {
        console.log('Sending admin notification for new user registration');
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-admin-registration`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            userEmail: email,
            userName: formData.fullName,
            organization: formData.organization
          })
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.warn('Failed to send admin notification:', errorData);
          throw new Error(errorData.error || '管理者への通知に失敗しました');
        }
        
        const responseData = await response.json();
        console.log('Admin notification response:', responseData);
      } catch (notifyError) {
        console.error('Error sending admin notification:', notifyError);
        // Continue with registration even if notification fails
      }

      try {
        const { error: notificationError } = await supabase
          .from('registration_notifications')
          .insert({
            user_email: email,
            user_name: formData.fullName,
            organization: formData.organization,
            status: 'approved', // 自動承認に変更
            created_at: new Date().toISOString()
          });
        
        if (notificationError) {
          console.error('Failed to save notification to database:', notificationError);
        } else {
          console.log('Registration notification saved to database');
        }
      } catch (dbError) {
        console.error('Error saving registration notification:', dbError);
      }

      // ログイン状態を維持して、サブスクリプションページに直接リダイレクト
      setSuccess(true);
      setTimeout(() => {
        navigate('/subscription');
      }, 2000);

    } catch (err) {
      console.error('Registration error:', err);
      let errorMessage = '登録に失敗しました';
      
      if (err instanceof Error) {
        if (err.message.includes('duplicate key')) {
          errorMessage = 'このメールアドレスは既に登録されています';
        } else if (err.message.includes('Password should be')) {
          errorMessage = 'パスワードは8文字以上である必要があります';
        } else if (err.message === 'パスワードが一致しません') {
          errorMessage = err.message;
        } else if (err.message === 'このメールアドレスは既に登録されています') {
          errorMessage = err.message;
        } else if (err.message.includes('既存のアカウントの削除に失敗')) {
          errorMessage = err.message;
        } else if (err.message === '管理者への通知に失敗しました') {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-primary-50 to-primary-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-lg shadow-xl p-8">
            <div className="text-center">
              <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                登録が完了しました
              </h2>
              <p className="text-gray-600 mb-4">
                サブスクリプションの登録ページに移動します...
              </p>
              <p className="text-sm text-gray-500">
                2秒後に自動的に移動します...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-primary-50 to-primary-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <Timer className="h-12 w-12 text-primary-600" />
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900">
              新規会員登録
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              アカウントを作成してください
            </p>
          </div>

          <div className="mb-6 bg-blue-50 p-4 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-700 font-medium">
              年額500円のサブスクリプションですが、初回登録から3ヶ月間は無料でご利用いただけます。登録後にお支払いページでお決済いただくと、3ヶ月後から年額プランが開始されます。
            </p>
            <div className="mt-3 text-xs text-blue-600">
              <ul className="list-disc list-inside space-y-1">
                <li>初回3ヶ月：完全無料</li>
                <li>4ヶ月目以降：年額500円（月額約41円）</li>
                <li>いつでもキャンセル可能</li>
              </ul>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                メールアドレス <span className="text-red-500">*</span>
              </label>
              <input
                id="email"
                type="email"
                required
                placeholder="example@email.com"
                value={formData.email}
                onChange={handleEmailChange}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg bg-gray-50 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:bg-white hover:border-gray-400 transition-all duration-200"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                パスワード <span className="text-red-500">*</span>
              </label>
              <input
                id="password"
                type="password"
                required
                placeholder="8文字以上で入力してください"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg bg-gray-50 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:bg-white hover:border-gray-400 transition-all duration-200"
                minLength={8}
              />
              <p className="mt-2 text-xs text-gray-500">
                8文字以上で入力してください
              </p>
            </div>

            <div>
              <label htmlFor="passwordConfirm" className="block text-sm font-semibold text-gray-700 mb-2">
                パスワード（確認） <span className="text-red-500">*</span>
              </label>
              <input
                id="passwordConfirm"
                type="password"
                required
                placeholder="上記と同じパスワードを入力"
                value={formData.passwordConfirm}
                onChange={(e) => setFormData({ ...formData, passwordConfirm: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg bg-gray-50 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:bg-white hover:border-gray-400 transition-all duration-200"
                minLength={8}
              />
            </div>

            <div>
              <label htmlFor="fullName" className="block text-sm font-semibold text-gray-700 mb-2">
                氏名 <span className="text-red-500">*</span>
              </label>
              <input
                id="fullName"
                type="text"
                required
                placeholder="山田太郎"
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg bg-gray-50 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:bg-white hover:border-gray-400 transition-all duration-200"
              />
            </div>

            <div>
              <label htmlFor="organization" className="block text-sm font-semibold text-gray-700 mb-2">
                所属組織 <span className="text-red-500">*</span>
              </label>
              <input
                id="organization"
                type="text"
                required
                placeholder="会社名、学校名など"
                value={formData.organization}
                onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg bg-gray-50 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:bg-white hover:border-gray-400 transition-all duration-200"
              />
            </div>

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">{error}</h3>
                  </div>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-md text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02] disabled:transform-none"
            >
              {loading ? '登録中...' : '登録する'}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-sm text-primary-600 hover:text-primary-700 hover:underline font-medium transition-colors duration-200"
              >
                すでにアカウントをお持ちの方はこちら
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;